import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CoPresentIcon from "@mui/icons-material/CoPresent";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import StoreIcon from "@mui/icons-material/Store";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import MoreHorizOutlinedIcon from "@mui/icons-material/MoreHorizOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";

import { NAVIGATION } from "../config/navigation/navigation.config";
import { NavIcon } from "../config/navigation/navigation.icons";

import {
  Box,
  Drawer,
  Toolbar,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CssBaseline,
  AppBar,
  Container,
  IconButton,
  useMediaQuery,
  Tooltip,
  Badge,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Popper,
  ClickAwayListener,
} from "@mui/material";

import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useAuth } from "../context/AuthContext";
import { useNotifications } from "../context/NotificationContext";
import { useThemeMode } from "../context/ThemeContext";
import NotificationsDrawer from "../components/General/NotificationsDrawer";
import Breadcrumb from "../components/General/Breadcrumb";
import LegalFileSearch from "../components/General/LegalFileSearch";
import { getCurrentBranch } from "../services/ActiveBranchService";
import CardHeader from "../components/General/CardHeader";
import ConfirmDialog from "../components/General/Dialog/ConfirmDialog";

const DESKTOP_RAIL_WIDTH = 140;
const MOBILE_BOTTOM_BAR_SPACE =
  "calc(72px + max(env(safe-area-inset-bottom), 8px))";

export default function DashboardLayoutBasic() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isDark = theme.palette.mode === "dark";

  const glassBg = isDark ? "rgba(15,23,42,0.88)" : "rgba(255,255,255,0.88)";
  const appBarBg = isDark ? "rgba(15,23,42,0.82)" : "rgba(255,255,255,0.78)";
  const hoverBg = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)";
  const borderColor = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)";
  const iconBg = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)";
  const iconBgSelected = isDark ? "rgba(255,255,255,0.14)" : "rgba(255,255,255,0.96)";
  const submenuBg = isDark ? "rgba(15,23,42,0.96)" : "rgba(255,255,255,0.94)";
  const bottomNavBg = isDark ? "rgba(15,23,42,0.97)" : "rgba(255,255,255,0.97)";

  const [mobileOpen, setMobileOpen] = useState(false);

  const { auth, isAuthenticated, hasPermission, logout, setActiveBranches } =
    useAuth();

  const { unreadCount } = useNotifications();
  const { toggleTheme } = useThemeMode();
  const [openNotifications, setOpenNotifications] = useState(false);

  const [branchDialogOpen, setBranchDialogOpen] = useState(false);
  const [branchPickerOpen, setBranchPickerOpen] = useState(false);
  const [branchCandidate, setBranchCandidate] = useState(null);

  const [submenuAnchorEl, setSubmenuAnchorEl] = useState(null);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const canSearchLegalFiles = hasPermission("buscar expediente");

  const getMobileTab = (pathname) => {
    if (pathname.startsWith("/home")) return "home";
    if (pathname.startsWith("/legalFile")) return "legalFile";
    return "home";
  };

  const [mobileTab, setMobileTab] = useState(() =>
    getMobileTab(location.pathname),
  );

  const lastNonMoreTabRef = useRef(mobileTab);

  useEffect(() => {
    const t = getMobileTab(location.pathname);
    setMobileTab(t);
    lastNonMoreTabRef.current = t;
  }, [location.pathname]);

  const isSelected = useCallback(
    (segment, match = "exact") => {
      const path = `/${segment}`;
      if (match === "exact") return location.pathname === path;
      return location.pathname.startsWith(path);
    },
    [location.pathname],
  );

  const filteredNavigation = useMemo(() => {
    const filterItem = (item) => {
      if (item.permission && !hasPermission(item.permission)) return null;

      if (Array.isArray(item.children)) {
        const children = item.children.map(filterItem).filter(Boolean);
        if (!children.length) return null;
        return { ...item, children };
      }

      return item;
    };

    return NAVIGATION.map(filterItem).filter(Boolean);
  }, [hasPermission]);

  const closeMobileDrawer = () => {
    setMobileOpen(false);
    const t = getMobileTab(location.pathname);
    setMobileTab(t);
    lastNonMoreTabRef.current = t;
  };

  const openMoreDrawer = () => setMobileOpen(true);

  const handleLogout = useCallback(() => {
    setLogoutConfirmOpen(true);
  }, []);

  const handleConfirmLogout = useCallback(() => {
    setLogoutConfirmOpen(false);
    logout();
    navigate("/");
  }, [logout, navigate]);

  useEffect(() => {
    if (!isAuthenticated()) navigate("/");
  }, [location, isAuthenticated, navigate]);

  useEffect(() => {
    const handleStorage = () => {
      const token = localStorage.getItem("token");
      if (!token) navigate("/");
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [navigate]);

  const availableBranches = useMemo(() => {
    const userBranches = auth?.user?.branches || [];
    return userBranches.filter((b) => b.is_active);
  }, [auth?.user]);

  useEffect(() => {
    if (!isAuthenticated()) return;

    const hasSelection = auth?.activeBranches && auth.activeBranches.length > 0;
    if (hasSelection) return;

    if (availableBranches.length === 1) {
      const onlyBranch = availableBranches[0];
      setActiveBranches([{ id: onlyBranch.id, name: onlyBranch.name }]);
      setBranchDialogOpen(false);
      return;
    }

    if (availableBranches.length > 1) setBranchDialogOpen(true);
  }, [
    isAuthenticated,
    auth?.activeBranches,
    availableBranches,
    setActiveBranches,
  ]);

  const currentBranch = auth?.activeBranches?.[0] || getCurrentBranch();

  const openBranchPicker = () => {
    setBranchCandidate(currentBranch || null);
    setBranchPickerOpen(true);
  };

  const closeBranchPicker = () => setBranchPickerOpen(false);

  const handleSelectBranch = (branch) => {
    if (!branch) return;
    setActiveBranches([{ id: branch.id, name: branch.name }]);
    setBranchDialogOpen(false);
    window.location.reload();
  };

  const confirmBranchChange = () => {
    if (!branchCandidate) return;
    setActiveBranches([{ id: branchCandidate.id, name: branchCandidate.name }]);
    setBranchPickerOpen(false);
    window.location.reload();
  };

  const handleOpenSubmenu = (event, item) => {
    if (isMobile) return;
    setSubmenuAnchorEl(event.currentTarget);
    setOpenSubmenu(item);
  };

  const handleCloseSubmenu = () => {
    setSubmenuAnchorEl(null);
    setOpenSubmenu(null);
  };

  const railPaperSx = {
    width: DESKTOP_RAIL_WIDTH,
    boxSizing: "border-box",
    overflowX: "hidden",
    borderRight: `1px solid ${borderColor}`,
    background: glassBg,
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
    boxShadow: "none",
    px: 1,
    py: 1,
  };

  const iconWrapSx = (selected) => ({
    width: 40,
    height: 40,
    borderRadius: "14px",
    display: "grid",
    placeItems: "center",
    bgcolor: selected ? iconBgSelected : iconBg,
    boxShadow: selected ? "0 8px 18px rgba(0,0,0,0.08)" : "none",
    transition: "all .2s ease",
    flexShrink: 0,
    "& svg": { fontSize: 22, display: "block" },
  });

  const railItemSx = (selected) => ({
    my: 0.45,
    mx: "auto",
    width: 120,
    minHeight: 74,
    borderRadius: 4,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: 0.7,
    cursor: "pointer",
    transition: "all .18s ease",
    color: selected ? "primary.main" : "text.secondary",
    bgcolor: selected ? "rgba(25,118,210,0.10)" : "transparent",
    border: selected
      ? "1px solid rgba(25,118,210,0.18)"
      : "1px solid transparent",
    "&:hover": {
      bgcolor: selected ? "rgba(25,118,210,0.14)" : hoverBg,
      transform: "translateY(-1px)",
    },
  });

  const mobileItemSx = (selected) => ({
    mx: 1,
    my: 0.55,
    borderRadius: 2.5,
    position: "relative",
    cursor: "pointer",
    transition: "background-color .2s ease, transform .06s ease",
    "&:hover": { bgcolor: hoverBg },
    ...(selected && {
      bgcolor: "rgba(25,118,210,0.10)",
      "&:hover": { bgcolor: "rgba(25,118,210,0.14)" },
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 8,
        bottom: 8,
        width: 4,
        borderRadius: 4,
        backgroundColor: "primary.main",
      },
    }),
  });

  const renderDesktopRail = () => (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: 0,
      }}
    >
      <Toolbar
        sx={{
          minHeight: "72px !important",
          width: "100%",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Box
          onClick={() => navigate("/home")}
          sx={{
            width: 48,
            height: 48,
            borderRadius: 3,
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(25,118,210,0.10)",
            color: "primary.main",
            fontWeight: 950,
            fontSize: 18,
            border: "1px solid rgba(25,118,210,0.18)",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          J
        </Box>
      </Toolbar>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          width: "100%",
          px: 0.25,
          pb: 1,
        }}
      >
        <List sx={{ py: 0.5 }}>
          {filteredNavigation.map((item) => {
            const { segment, title, children } = item;
            const isParent = Array.isArray(children) && children.length > 0;
            const selected = isParent
              ? location.pathname.startsWith(`/${segment}`)
              : isSelected(segment, item.match || "exact");

            return (
              <Tooltip title={title} placement="right" key={segment}>
                <ListItemButton
                  onClick={(e) => {
                    if (isParent) {
                      handleOpenSubmenu(e, item);
                      return;
                    }

                    navigate(`/${segment}`);
                    handleCloseSubmenu();
                  }}
                  sx={railItemSx(selected)}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                    }}
                  >
                    <Box sx={iconWrapSx(selected)}>
                      <NavIcon iconKey={item.iconKey} />
                    </Box>

                    <Typography
                      sx={{
                        mt: 0.6,
                        fontSize: 11,
                        fontWeight: selected ? 800 : 650,
                        lineHeight: 1.1,
                        textAlign: "center",
                        maxWidth: DESKTOP_RAIL_WIDTH,
                        color: selected ? "primary.main" : "text.secondary",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        wordBreak: "break-word",
                      }}
                    >
                      {title}
                    </Typography>
                  </Box>
                </ListItemButton>
              </Tooltip>
            );
          })}
        </List>
      </Box>
    </Box>
  );

  const renderMobileDrawer = () => (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>

      {/* Barra utilitaria compacta */}
      <Box
        sx={{
          px: 1.5,
          height: 68,
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
          borderBottom: `1px solid ${borderColor}`,
        }}
      >
        <Tooltip title={isDark ? "Modo claro" : "Modo oscuro"}>
          <IconButton
            onClick={toggleTheme}
            sx={{ borderRadius: 2.5, "&:hover": { bgcolor: hoverBg } }}
          >
            {isDark ? <LightModeRoundedIcon sx={{ fontSize: 22 }} /> : <DarkModeRoundedIcon sx={{ fontSize: 22 }} />}
          </IconButton>
        </Tooltip>

        {currentBranch && (
          <Button
            onClick={openBranchPicker}
            size="small"
            variant="outlined"
            startIcon={<AccountBalanceIcon sx={{ fontSize: 16 }} />}
            sx={{
              flex: 1,
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 700,
              fontSize: 13,
              px: 1.5,
              py: 0.6,
              "& .MuiButton-startIcon": { mr: 0.5 },
            }}
          >
            <Typography noWrap sx={{ fontSize: 13, fontWeight: 700 }}>
              {currentBranch.name}
            </Typography>
          </Button>
        )}

        {hasPermission("procesos") && (
          <Tooltip title="Procesos">
            <IconButton
              onClick={() => { navigate("/job/history"); closeMobileDrawer(); }}
              sx={{ borderRadius: 2.5, "&:hover": { bgcolor: hoverBg } }}
            >
              <PublishedWithChangesIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Lista de navegación */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", py: 0.5 }}>
        <List sx={{ px: 1, py: 0.5 }}>
          {filteredNavigation.map((item) => {
            const { segment, title, children } = item;
            const isParent = Array.isArray(children) && children.length > 0;

            if (isParent) {
              return (
                <Box key={segment}>
                  <Typography
                    sx={{
                      px: 2,
                      pt: 1.5,
                      pb: 0.4,
                      fontSize: 10.5,
                      fontWeight: 900,
                      letterSpacing: 0.9,
                      textTransform: "uppercase",
                      color: "text.disabled",
                    }}
                  >
                    {title}
                  </Typography>
                  {item.children.map((child) => {
                    const childSelected = isSelected(child.segment, child.match || "prefix");
                    return (
                      <ListItemButton
                        key={child.segment}
                        onClick={() => { navigate(`/${child.segment}`); closeMobileDrawer(); }}
                        sx={mobileItemSx(childSelected)}
                      >
                        <ListItemIcon sx={{ minWidth: 48, mr: 1, display: "flex", justifyContent: "center" }}>
                          <Box sx={iconWrapSx(childSelected)}>
                            <NavIcon iconKey={child.iconKey} />
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          primary={child.title}
                          primaryTypographyProps={{ fontSize: 14.5, fontWeight: childSelected ? 800 : 600 }}
                        />
                      </ListItemButton>
                    );
                  })}
                </Box>
              );
            }

            const selected = isSelected(segment, item.match || "exact");
            return (
              <ListItemButton
                key={segment}
                onClick={() => { navigate(`/${segment}`); closeMobileDrawer(); }}
                sx={mobileItemSx(selected)}
              >
                <ListItemIcon sx={{ minWidth: 48, mr: 1, display: "flex", justifyContent: "center" }}>
                  <Box sx={iconWrapSx(selected)}>
                    <NavIcon iconKey={item.iconKey} />
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={title}
                  primaryTypographyProps={{ fontSize: 14.5, fontWeight: selected ? 800 : 600 }}
                />
              </ListItemButton>
            );
          })}

          {/* Cerrar sesión integrado igual que los demás ítems */}
          <ListItemButton onClick={handleLogout} sx={mobileItemSx(false)}>
            <ListItemIcon sx={{ minWidth: 48, mr: 1, display: "flex", justifyContent: "center" }}>
              <Box sx={iconWrapSx(false)}>
                <CoPresentIcon />
              </Box>
            </ListItemIcon>
            <ListItemText
              primary="Cerrar sesión"
              primaryTypographyProps={{ fontSize: 14.5, fontWeight: 600 }}
            />
          </ListItemButton>
        </List>
      </Box>
    </Box>
  );

  const BranchPickerDialog = ({ open, required }) => (
    <Dialog
      open={open}
      onClose={required ? () => {} : closeBranchPicker}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}
      BackdropProps={{
        sx: {
          background: "rgba(255,255,255,0.10)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        },
      }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <CardHeader
          icon={<StoreIcon />}
          title="Seleccionar sucursal"
          description="Elegí la sucursal con la que vas a trabajar."
        />
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Autocomplete
          options={availableBranches}
          value={required ? null : branchCandidate}
          onChange={(_, value) => {
            if (required) handleSelectBranch(value);
            else setBranchCandidate(value);
          }}
          sx={{ mt: 2 }}
          getOptionLabel={(option) => option?.name || ""}
          isOptionEqualToValue={(a, b) => a?.id === b?.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Buscar sucursal"
              placeholder="Escribí para filtrar…"
            />
          )}
          renderOption={(props, option) => {
            const selected = required
              ? currentBranch?.id === option.id
              : branchCandidate?.id === option.id;

            return (
              <Box
                component="li"
                {...props}
                key={option.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.2,
                  py: 1.1,
                  px: 1,
                  borderRadius: 2,
                  width: "100%",
                  ...(selected && { bgcolor: "rgba(25,118,210,0.10)" }),
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "14px",
                    display: "grid",
                    placeItems: "center",
                    bgcolor: selected
                      ? "rgba(25,118,210,0.14)"
                      : iconBg,
                  }}
                >
                  <AccountBalanceIcon sx={{ fontSize: 20 }} />
                </Box>

                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography sx={{ fontWeight: 800 }} noWrap>
                    {option.name}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }} noWrap>
                    ID: {option.id}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: 999,
                    bgcolor: selected ? "primary.main" : "rgba(0,0,0,0.15)",
                  }}
                />
              </Box>
            );
          }}
        />
      </DialogContent>

      {!required && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeBranchPicker} sx={{ borderRadius: 2 }}>
            Cancelar
          </Button>
          <Button
            onClick={confirmBranchChange}
            variant="contained"
            disabled={
              !branchCandidate || branchCandidate?.id === currentBranch?.id
            }
            sx={{ borderRadius: 2, fontWeight: 800 }}
          >
            Cambiar sucursal
          </Button>
        </DialogActions>
      )}

      {required && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Typography variant="caption" sx={{ opacity: 0.75 }}>
            Debe seleccionar una sucursal para continuar trabajando.
          </Typography>
        </DialogActions>
      )}
    </Dialog>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      <AppBar
        position="fixed"
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          background: appBarBg,
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          boxShadow: "none",
          borderBottom: `1px solid ${borderColor}`,
        }}
      >
        <Toolbar sx={{ minHeight: "72px !important", position: "relative" }}>

          <Typography
            onClick={() => navigate("/home")}
            sx={{
              fontWeight: 950,
              fontSize: isMobile ? 22 : 28,
              letterSpacing: 0.6,
              paddingLeft: isMobile ? 0 : 2.5,
              cursor: "pointer",
              color: "primary.main",
              ...(isMobile && {
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
              }),
            }}
          >
            JEX
          </Typography>

          {!isMobile && canSearchLegalFiles && (
            <Box
              sx={{
                ml: 3,
                flexGrow: 1,
                maxWidth: 460,
                minWidth: 220,
              }}
            >
              <LegalFileSearch variant="navbar" />
            </Box>
          )}

          {isMobile && canSearchLegalFiles && (
            <Tooltip title="Buscar expediente">
              <IconButton
                onClick={() => setMobileSearchOpen(true)}
                sx={{
                  marginLeft: "auto",
                  borderRadius: 2.5,
                  "&:hover": { bgcolor: hoverBg },
                }}
              >
                <SearchRoundedIcon />
              </IconButton>
            </Tooltip>
          )}

          {!isMobile && (
            <Box
              sx={{
                marginLeft: "auto",
                display: "flex",
                gap: 1,
                alignItems: "center",
              }}
            >
              {currentBranch && (
                <Tooltip title="Cambiar sucursal">
                  <Button
                    onClick={openBranchPicker}
                    variant="outlined"
                    size="small"
                    sx={{
                      borderRadius: 999,
                      textTransform: "none",
                      px: 1.2,
                      gap: 1,
                      bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.82)",
                      borderColor: "rgba(25,118,210,0.18)",
                      "&:hover": {
                        bgcolor: isDark ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.98)",
                        borderColor: "rgba(25,118,210,0.34)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: "10px",
                        display: "grid",
                        placeItems: "center",
                        bgcolor: "rgba(25,118,210,0.10)",
                        border: "1px solid rgba(25,118,210,0.16)",
                      }}
                    >
                      <AccountBalanceIcon sx={{ fontSize: 18 }} />
                    </Box>

                    <Typography
                      variant="body2"
                      noWrap
                      sx={{ fontWeight: 800, maxWidth: 180 }}
                    >
                      {currentBranch.name}
                    </Typography>
                  </Button>
                </Tooltip>
              )}

              <Tooltip title={isDark ? "Modo claro" : "Modo oscuro"}>
                <IconButton
                  onClick={toggleTheme}
                  sx={{
                    borderRadius: 2.5,
                    "&:hover": { bgcolor: hoverBg },
                  }}
                >
                  {isDark ? <LightModeRoundedIcon /> : <DarkModeRoundedIcon />}
                </IconButton>
              </Tooltip>

              <Tooltip title="Notificaciones">
                <IconButton
                  onClick={() => setOpenNotifications((prev) => !prev)}
                  sx={{
                    borderRadius: 2.5,
                    "&:hover": { bgcolor: hoverBg },
                  }}
                >
                  <Badge badgeContent={unreadCount} color="error">
                    <NotificationsNoneOutlinedIcon />
                  </Badge>
                </IconButton>
              </Tooltip>

              {hasPermission("procesos") && (
                <Tooltip title="Procesos">
                  <IconButton
                    onClick={() => navigate("/job/history")}
                    sx={{
                      borderRadius: 2.5,
                      "&:hover": { bgcolor: hoverBg },
                    }}
                  >
                    <PublishedWithChangesIcon />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title="Cerrar sesión">
                <IconButton
                  onClick={handleLogout}
                  sx={{
                    borderRadius: 2.5,
                    "&:hover": { bgcolor: hoverBg },
                  }}
                >
                  <CoPresentIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={closeMobileDrawer}
          ModalProps={{
            keepMounted: true,
            BackdropProps: { sx: { top: "72px", backgroundColor: "transparent" } },
          }}
          sx={{
            [`& .MuiDrawer-paper`]: {
              width: "100vw",
              boxSizing: "border-box",
              top: "72px",
              height: "calc(100dvh - 144px)",
              borderRadius: 0,
              border: "none",
              background: isDark ? "#0f172a" : "#ffffff",
              boxShadow: "none",
            },
          }}
        >
          {renderMobileDrawer()}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: DESKTOP_RAIL_WIDTH,
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: railPaperSx,
          }}
        >
          {renderDesktopRail()}
        </Drawer>
      )}

      {!isMobile && (
        <Popper
          open={Boolean(openSubmenu && submenuAnchorEl)}
          anchorEl={submenuAnchorEl}
          placement="right-start"
          sx={{ zIndex: (t) => t.zIndex.drawer + 20 }}
        >
          <ClickAwayListener onClickAway={handleCloseSubmenu}>
            <Paper
              elevation={0}
              sx={{
                ml: 1.2,
                minWidth: 270,
                p: 1,
                borderRadius: 4,
                border: `1px solid ${borderColor}`,
                background: submenuBg,
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
                boxShadow: "0 18px 50px rgba(0,0,0,0.12)",
              }}
            >
              <Typography
                sx={{
                  px: 1.2,
                  py: 1,
                  fontWeight: 900,
                  fontSize: 14,
                  opacity: 0.8,
                }}
              >
                {openSubmenu?.title}
              </Typography>

              <List disablePadding>
                {openSubmenu?.children?.map((child) => {
                  const childSelected = isSelected(
                    child.segment,
                    child.match || "prefix",
                  );

                  return (
                    <ListItemButton
                      key={child.segment}
                      onClick={() => {
                        navigate(`/${child.segment}`);
                        handleCloseSubmenu();
                      }}
                      sx={{
                        borderRadius: 3,
                        mb: 0.4,
                        bgcolor: childSelected
                          ? "rgba(25,118,210,0.10)"
                          : "transparent",
                        "&:hover": {
                          bgcolor: childSelected
                            ? "rgba(25,118,210,0.14)"
                            : hoverBg,
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 42 }}>
                        <Box sx={iconWrapSx(childSelected)}>
                          <NavIcon iconKey={child.iconKey} />
                        </Box>
                      </ListItemIcon>

                      <ListItemText
                        primary={child.title}
                        primaryTypographyProps={{
                          fontSize: 14,
                          fontWeight: childSelected ? 800 : 650,
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          pb: isMobile ? MOBILE_BOTTOM_BAR_SPACE : 0,
        }}
      >
        <Toolbar sx={{ minHeight: "72px !important" }} />
        <Breadcrumb />
        <Container maxWidth="lg" sx={{ mb: 4 }}>
          <Outlet />
        </Container>

        <NotificationsDrawer
          open={openNotifications}
          onClose={() => {
            setOpenNotifications(false);
            setMobileTab(getMobileTab(location.pathname));
          }}
        />
      </Box>

      {isMobile && (
        <Paper
          elevation={0}
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: (t) => t.zIndex.drawer + 2,
            bgcolor: bottomNavBg,
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            borderRadius: 0,
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            height: "calc(72px + env(safe-area-inset-bottom))",
            overflow: "hidden",
            boxShadow: isDark
              ? "0 -8px 32px rgba(0,0,0,0.32)"
              : "0 -8px 28px rgba(0,0,0,0.07)",
          }}
        >
          <BottomNavigation
            value={mobileTab}
            showLabels
            onChange={(_, newValue) => {
              if (newValue === "notifications") {
                if (mobileTab === "notifications") {
                  setOpenNotifications(false);
                  setMobileTab(getMobileTab(location.pathname));
                } else {
                  setMobileTab("notifications");
                  setOpenNotifications(true);
                }
                return;
              }
              if (newValue === "more") {
                if (mobileTab === "more") {
                  closeMobileDrawer();
                } else {
                  setMobileTab("more");
                  openMoreDrawer();
                }
                return;
              }
              lastNonMoreTabRef.current = newValue;
              setMobileTab(newValue);
              const routes = { home: "/home", legalFile: "/legalFile" };
              const to = routes[newValue];
              if (to) navigate(to);
            }}
            sx={{
              height: "100%",
              pb: "env(safe-area-inset-bottom)",
              bgcolor: "transparent",
              "& .MuiBottomNavigationAction-root": {
                minWidth: 0,
                px: 0.5,
                color: isDark ? "rgba(255,255,255,0.38)" : "rgba(0,0,0,0.35)",
                transition: "color .2s ease",
                "& .MuiBottomNavigationAction-label": {
                  fontSize: "10.5px !important",
                  fontWeight: "700 !important",
                  letterSpacing: 0.2,
                  mt: 0.25,
                  opacity: "1 !important",
                },
              },
              "& .MuiBottomNavigationAction-root.Mui-selected": {
                color: "primary.main",
              },
            }}
          >
            {[
              { value: "home", icon: <HomeOutlinedIcon />, label: "Inicio" },
              {
                value: "notifications",
                icon: (
                  <Badge badgeContent={unreadCount} color="error">
                    <NotificationsNoneOutlinedIcon />
                  </Badge>
                ),
                label: "Alertas",
              },
              hasPermission("expediente") && {
                value: "legalFile",
                icon: <FolderOutlinedIcon />,
                label: "Expedientes",
              },
              { value: "more", icon: <MoreHorizOutlinedIcon />, label: "Más" },
            ]
              .filter(Boolean)
              .map(({ value, icon, label }) => (
                <BottomNavigationAction
                  key={value}
                  value={value}
                  label={label}
                  icon={
                    <Box
                      sx={{
                        width: 52,
                        height: 28,
                        borderRadius: 999,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "background .22s ease",
                        bgcolor:
                          mobileTab === value
                            ? isDark
                              ? "rgba(25,118,210,0.22)"
                              : "rgba(25,118,210,0.12)"
                            : "transparent",
                        "& .MuiSvgIcon-root": { fontSize: 22 },
                      }}
                    >
                      {icon}
                    </Box>
                  }
                />
              ))}
          </BottomNavigation>
        </Paper>
      )}

      <BranchPickerDialog open={branchDialogOpen} required />
      <BranchPickerDialog open={branchPickerOpen} required={false} />

      <Dialog
        open={mobileSearchOpen}
        onClose={() => setMobileSearchOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 4,
            position: "fixed",
            top: 84,
            m: 1.5,
            width: "calc(100% - 24px)",
          },
        }}
        BackdropProps={{
          sx: {
            background: isDark ? "rgba(0,0,0,0.5)" : "rgba(15,23,42,0.18)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
          },
        }}
      >
        <DialogContent sx={{ p: 2 }}>
          <LegalFileSearch
            variant="navbar"
            autoFocus
            onSelected={() => setMobileSearchOpen(false)}
          />
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={logoutConfirmOpen}
        onClose={() => setLogoutConfirmOpen(false)}
        onConfirm={handleConfirmLogout}
        title="Cerrar sesión"
        description="¿Estás seguro que querés cerrar la sesión?"
        confirmText="Cerrar sesión"
        cancelText="Cancelar"
        confirmColor="error"
      />
    </Box>
  );
}
